import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, ShieldCheck, KeyRound, Lock } from 'lucide-react';
interface SlideObjectionsProps {
  onContinue: () => void;
}
const DEPENDENCIES = ['Agencies', 'Internal teams', 'Technology', 'Production partners', 'Data providers', 'Compliance systems'];
const ACCESS = ['All campaign content', 'All Snaks & approved assets', 'All storyboards', 'All local versions', 'All campaign data', 'All performance reporting', 'Standardized workflows', 'The HQ Control Tower'];
export function SlideObjections({
  onContinue
}: SlideObjectionsProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [300, 900, 1600, 2400].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-882" data-mp="139a40s09cvsi">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-3 shrink-0" data-id="element-883" data-mp="1g3iakmi7oz1a">
        <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-884" data-mp="16dfh6l76hj2f">
          But… the objection
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-white" data-id="element-885" data-mp="11fneqx8wqejz">
          "Doesn't this make us dependent on Snakker?"
        </h2>
        <p className="text-lg text-white/60" data-id="element-886" data-mp="1z1kzz2eo8xzm">
          Dependency becomes managed capability — with more control, not less.
        </p>
      </motion.div>

      <div className="flex-1 min-h-0 flex flex-col justify-center max-w-6xl mx-auto w-full space-y-5 mt-6" data-id="element-887" data-mp="1p3f16to5w7yz">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-id="element-888" data-mp="1l1js32zzoqan">
          {/* Every model depends */}
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          y: stage >= 1 ? 0 : 14
        }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6" data-id="element-889" data-mp="dq2wyhohpnca">
            <div className="w-10 h-10 rounded-xl bg-navy-700 text-white/60 flex items-center justify-center mb-4" data-id="element-890" data-mp="g36ejek59oea">
              <HelpCircle className="w-5 h-5" data-id="element-891" data-mp="18i920equpyo" />
            </div>
            <h3 className="text-white font-semibold mb-1" data-id="element-892" data-mp="647doi5mex1u">
              Every model depends on capabilities
            </h3>
            <p className="text-white/50 text-sm mb-4 leading-relaxed" data-id="element-893" data-mp="3oi6vpdhujcn8">
              The question was never whether there is a dependency — but whether
              it is transparent, governed, measurable, replaceable and
              cost-controlled.
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-894" data-mp="3jlgdajikk4yn">
              {DEPENDENCIES.map(item => <span key={item} className="px-3 py-1.5 rounded-full border border-navy-600 text-white/60 text-xs font-medium" data-id="element-895" data-mp="35low69wra8gf">
                  {item}
                </span>)}
            </div>
          </motion.div>

          {/* The two real worries */}
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          y: stage >= 2 ? 0 : 14
        }} className="space-y-4" data-id="element-896" data-mp="2m0ooe1ruxstx">
            <div className="bg-navy-700/40 border border-navy-600 rounded-2xl p-5" data-id="element-897" data-mp="39vrf6fa826d1">
              <div className="flex items-center gap-2.5 mb-2" data-id="element-898" data-mp="34yhs36ab26sd">
                <Lock className="w-4 h-4 text-rose-400 shrink-0" data-id="element-899" data-mp="34vvhsyw15d6g" />
                <p className="text-rose-400 font-semibold text-xs uppercase tracking-widest" data-id="element-900" data-mp="3iol8hgmi769w">
                  Worry 1 — Lock-in
                </p>
              </div>
              <p className="text-white font-semibold text-sm mb-1.5" data-id="element-901" data-mp="3smhig53pfv6f">
                "We won't be able to move."
              </p>
              <p className="text-white/70 text-sm leading-relaxed" data-id="element-902" data-mp="3npqyh6os4ffq">
                Everything is exit-ready by design: transferable assets,
                documented campaigns, standardized workflows and full data
                export — so the capability can be moved in-house or to another
                partner at any renewal point.
              </p>
            </div>

            <div className="bg-navy-700/40 border border-navy-600 rounded-2xl p-5" data-id="element-903" data-mp="pb9o68bp0x4f">
              <div className="flex items-center gap-2.5 mb-2" data-id="element-904" data-mp="3os7yvc6w70ip">
                <ShieldCheck className="w-4 h-4 text-sky-400 shrink-0" data-id="element-905" data-mp="3otstf1t7ijgk" />
                <p className="text-sky-400 font-semibold text-xs uppercase tracking-widest" data-id="element-906" data-mp="3stbdxwh48siv">
                  Worry 2 — Price escalation
                </p>
              </div>
              <p className="text-white font-semibold text-sm mb-1.5" data-id="element-907" data-mp="6mv09two9z7u">
                "The vendor can raise the price later."
              </p>
              <p className="text-white/70 text-sm leading-relaxed" data-id="element-908" data-mp="1d49ndxd9b9gp">
                Handled by an{' '}
                <span className="text-teal-400 font-semibold" data-id="element-909" data-mp="1i2t9pyvrin9l">
                  enterprise fixed-cost agreement
                </span>{' '}
                — a fixed annual price for an agreed portfolio scope, with unit
                economics and service levels locked for the term. No per-request
                pricing, no surprise escalation.
              </p>
            </div>
          </motion.div>

          {/* What LEO gets */}
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          y: stage >= 3 ? 0 : 14
        }} className="bg-teal-400/10 border border-teal-400/30 rounded-2xl p-6" data-id="element-910" data-mp="2jtksztij5fdh">
            <div className="w-10 h-10 rounded-xl bg-teal-400/20 text-teal-400 flex items-center justify-center mb-4" data-id="element-911" data-mp="24wtr9dldvrkp">
              <KeyRound className="w-5 h-5" data-id="element-912" data-mp="80wb4biyozzu" />
            </div>
            <h3 className="text-white font-semibold mb-1" data-id="element-913" data-mp="34ywqar9e3pp">
              LEO owns every output
            </h3>
            <p className="text-white/50 text-sm mb-4 leading-relaxed" data-id="element-914" data-mp="1xjcgv5vvdmcr">
              Full access, transferable assets, visible costs, agreed service
              levels.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-915" data-mp="1nmzku1cuqweu">
              {ACCESS.map(item => <div key={item} className="flex items-start gap-2 text-white/85 text-xs bg-navy-900/50 border border-navy-700 rounded-lg px-2.5 py-2 leading-snug" data-id="element-916" data-mp="1slt1g2nfr3j8">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0 mt-1" data-id="element-917" data-mp="2l8x96fdwxvyp" />
                  {item}
                </div>)}
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 12
      }} className="bg-navy-900/50 border border-navy-700 rounded-2xl px-7 py-5" data-id="element-918" data-mp="2bdlu1vylemqf">
          <p className="text-white/40 text-xs font-semibold uppercase tracking-widest mb-4" data-id="element-919" data-mp="sexp93dj048d">
            Why execution stays with Snakker
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-920" data-mp="12baik055sg2l">
            {[{
            step: '01',
            text: 'Our platform is judged on impact — not on assets delivered.'
          }, {
            step: '02',
            text: 'Impact depends on how campaigns are adapted, deployed and iterated, not on how they were made.'
          }, {
            step: '03',
            text: 'Self-service execution inside the platform breaks that loop and degrades outcomes.'
          }].map(item => <div key={item.step} className="flex items-start gap-3" data-id="element-921" data-mp="3n07zuwspfu2d">
                <span className="text-teal-400 font-semibold text-sm shrink-0" data-id="element-922" data-mp="3i2evm9aypa4q">
                  {item.step}
                </span>
                <p className="text-white/80 text-sm leading-relaxed" data-id="element-923" data-mp="2wk56vrlmlris">
                  {item.text}
                </p>
              </div>)}
          </div>
          <p className="text-white/90 text-base md:text-lg leading-relaxed mt-5 pt-5 border-t border-navy-700" data-id="element-924" data-mp="36gi0yql327e2">
            It is not that LEO could not run it.{' '}
            <span className="text-teal-400 font-semibold" data-id="element-925" data-mp="3ag9zsbkocz68">
              Outcomes are simply better when the expert execution loop stays
              with Snakker.
            </span>{' '}
            LEO keeps the steering wheel; we keep the engine running.
          </p>
        </motion.div>
      </div>
    </div>;
}